import { getRequest } from "lib/core/apiClient";
import { UserHistoryType, UserType } from "lib/types/user";
import { formatAllAddresses, getAddressById } from "lib/utils/address";
import { constants, eventTypes } from "lib/utils/constants";
import { publishPostMessage } from "lib/utils/helpers";
import { setCheckoutUserType } from "./checkout";
import { AddressConfigType } from "lib/contexts/MerchantProvider";

export const parseUserAddress = (
  checkoutAddresses: {
    shipping_address_id?: string;
    billing_address_id?: string;
  },
  addressResponse: Record<string, any>[],
  addressConfig?: AddressConfigType[],
) => {
  const areAdressesSame = checkoutAddresses?.shipping_address_id === checkoutAddresses?.billing_address_id;

  let shippingAddress;
  let billingAddress;

  shippingAddress = getAddressById(
    checkoutAddresses?.shipping_address_id ?? "",
    addressResponse,
    "SHIPPING_ADDRESS",
  );

  if (!areAdressesSame) {
    billingAddress = getAddressById(
      checkoutAddresses?.billing_address_id ?? "",
      addressResponse,
      "BILLING_ADDRESS",
    );
  }

  return {
    addresses: formatAllAddresses(addressResponse, shippingAddress?.id ?? "", addressConfig) ?? [],
    default_billing_address: areAdressesSame ? shippingAddress : billingAddress ?? {},
    default_shipping_address: shippingAddress ?? {},
  };
};

export const parseCustomerAttributes = (accountResponse: any) => {
  const firstName: string = accountResponse?.first_name ?? "";
  const lastName: string = accountResponse?.last_name ?? "";

  // Prefer first_name + last_name (auth DB, always fresh) over
  // customer_config.attributes.name (Kratos-synced, best-effort, may lag after updates)
  const name =
    firstName || lastName
      ? [firstName, lastName].filter(Boolean).join(" ")
      : accountResponse?.customer_config?.attributes?.name;

  const userData: UserType = {
    uid: accountResponse?.id,
    email: accountResponse?.customer_config?.email ?? accountResponse?.email,
    phone: accountResponse?.phone_number,
    name,
    marketing_consent: accountResponse?.customer_config?.attributes?.marketing_consent,
    authIdentifiers: accountResponse?.auth_identifiers ?? [],
    metadata: {
      first_name: firstName,
      last_name: lastName,
    },
  };

  return userData;
};

export const getUserOrderHistory = (merchantId: string): UserHistoryType => {
  const orderHistory = localStorage.getItem(constants.ORDER_HISTORY_COOKIE_CLIENT);
  if (!orderHistory || !Boolean(orderHistory)) {
    return "NA";
  }
  const orderHistoryObj = JSON.parse(orderHistory)[merchantId];
  if (!orderHistoryObj || !Boolean(orderHistoryObj)) {
    return "NA";
  }
  if (orderHistoryObj?.orders_count > 0) {
    return "OLD";
  }
  return "NEW";
};

export const setUserOrderHistory = async (accountId: string, merchantId: string, checkoutId: string) => {
  const userOrderHistory = getUserOrderHistory(merchantId);
  if (userOrderHistory === "OLD") {
    setCheckoutUserType(checkoutId, userOrderHistory);
    const orderHistory = localStorage.getItem(constants.ORDER_HISTORY_COOKIE_CLIENT);
    if (orderHistory) {
      const orderHistoryObj = JSON.parse(orderHistory)[merchantId] ?? {};
      publishPostMessage(eventTypes.FLO_GHOST_USER_ORDER_DATA, {
        isNewUser: false,
        ordersCount: orderHistoryObj?.orders_count,
        ordersTotal: orderHistoryObj?.orders_total,
      });
    }

    return;
  }
  try {
    const orderHistoryResponse = await getRequest(
      `/oms/merchant/${merchantId}/customer/${accountId}/order-context?status=COMPLETED`,
      "KRATOS_PRIVATE",
    );
    if (orderHistoryResponse?.status === 401 || orderHistoryResponse?.status === 403) {
      throw new Error("unauthorized");
    }
    const orderHistory = localStorage.getItem(constants.ORDER_HISTORY_COOKIE_CLIENT);
    const orderHistoryObj = orderHistory ? JSON.parse(orderHistory) : {};
    orderHistoryObj[merchantId] = orderHistoryResponse;
    localStorage.setItem(constants.ORDER_HISTORY_COOKIE_CLIENT, JSON.stringify(orderHistoryObj));
    setCheckoutUserType(checkoutId, Boolean(orderHistoryResponse?.orders_count) ? "OLD" : "NEW");
    publishPostMessage(eventTypes.FLO_GHOST_USER_ORDER_DATA, {
      isNewUser: !Boolean(orderHistoryResponse?.orders_count),
      ordersCount: orderHistoryResponse?.orders_count,
      ordersTotal: orderHistoryResponse?.orders_total,
    });
  } catch (error) {
    console.error(error);
  }
};
